import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { ReactComponent as TrevexLogo } from "../../../assets/images/logo.svg";
import { createVerifiedCheckoutSession, fetchCompanySummary } from "./api";
import { Dirham } from "../../common/Money";
import "./styles.css";
import { useLocation, useNavigate } from "react-router-dom";

// TIP-464 — Post-activation "Become a Verified company" payment screen,
// now backed by a real Stripe Embedded Checkout. Card details + billing
// address are collected inside Stripe's iframe (PCI scope stays on Stripe).
// Webhook routing at app/billing/views.py StripeWebhook → handle_verified_*
// provisions the Subscription + VerifiedCompany rows post-payment.

const PLAN_PRICES = { monthly: 99, yearly: 999 };

// Stripe client SDK — loaded once at module init. Requires
// REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY in frontend/.env.local; if
// missing, loadStripe(undefined) returns null and the iframe renders an
// error instead of mounting (preferable to silent failure).
const stripePromise = loadStripe(process.env.REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY);

// TIP-647 — `publicMode` drives the company-less variant mounted at the public
// /membership route: no company has been claimed yet, so the hero drops the
// "You've claimed X" line, the redirect-home guard is disabled, and there is no
// "stay on Claimed plan" to skip to. `onProceed` lets that page interpose its
// own gate (login → claim → company picker) in place of going straight to
// Stripe; /membership/:companyId and the verify-account flow pass neither and
// keep today's behaviour exactly.
function PaymentScreen({
  lead,
  token,
  onSkip,
  companyId: companyIdProp,
  publicMode = false,
  onProceed,
  viewerOwnsCompany,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  // TIP-488: companyID can arrive from the reusable /membership/:companyId
  // route (prop) or from the verify-account flow's navigation state.
  const companyID = companyIdProp || location.state?.currentCompanyId;
  // Set by the public /membership page's company picker (TIP-647). The visitor
  // came through the public flow, not account activation, so the activation
  // flow's "Final step" badge doesn't apply to them.
  const fromMembershipPage = Boolean(location.state?.fromMembershipPage);
  const verifyData = lead?.verify_data || {};
  // TIP-650: every /membership/:companyId entry point except the TIP-647 picker
  // passes only the id, so look the company up rather than falling through to
  // the "your business" placeholder. The owner gate (below) needs `claimed`
  // too, so it looks up even when router state already carried a name.
  // Skipped for the verify-account flow (`token`), which has the lead payload.
  const gated = viewerOwnsCompany !== undefined;
  const needsLookup =
    !publicMode && !token && Boolean(companyID)
    && (gated || !location.state?.companyName);
  // status: idle | loading | ok | missing
  const [summary, setSummary] = useState({ status: "idle", name: null, claimed: false });
  useEffect(() => {
    if (!needsLookup) return undefined;
    let cancelled = false;
    setSummary({ status: "loading", name: null, claimed: false });
    fetchCompanySummary(companyID)
      .then((s) => { if (!cancelled) setSummary({ status: "ok", ...s }); })
      // An id that doesn't resolve keeps the neutral wording below.
      .catch(() => {
        if (!cancelled) setSummary({ status: "missing", name: null, claimed: false });
      });
    return () => { cancelled = true; };
  }, [needsLookup, companyID]);
  const companyName =
    location.state?.companyName
    || (token ? (verifyData.company?.name || "Your business") : "")
    || summary.name
    || (companyID ? "your business" : "");

  // TIP-650 scope addition — only the company's owner can buy membership.
  // Membership.jsx passes viewerOwnsCompany; callers that don't (the
  // verify-account flow, the public page) keep today's checkout.
  //   checkout    — owner, or an ungated caller
  //   checking    — ownership or the company lookup not answered yet
  //   unclaimed   — nobody owns it: claim it first (free)
  //   otherOwner  — claimed by someone else, or the visitor is only an Editor
  //   missing     — the id doesn't resolve
  // UX only: the server-side ownership check on checkout is TIP-660.
  let access = "checkout";
  if (gated && viewerOwnsCompany !== true) {
    if (viewerOwnsCompany === null) access = "checking";
    else if (summary.status === "missing") access = "missing";
    else if (summary.status !== "ok") access = "checking";
    else access = summary.claimed ? "otherOwner" : "unclaimed";
  }
  const showCheckout = access === "checkout";
  // The gate states name the company without the "your business" fallback,
  // which would read "your business hasn't been claimed yet".
  const gateName = summary.name || location.state?.companyName || "This business";

  // Two stages of the screen:
  //   'pick'   — hero + benefits + price card with Pay/Skip buttons
  //   'paying' — same hero + benefits, but the price card is replaced
  //              by the Stripe iframe. "Change plan" link goes back.
  // TIP-494: the dashboard buy entry can preselect a billing cycle via router
  // state (state: { plan: "monthly" | "yearly" }); default to yearly otherwise.
  // TIP-647: PrivateRoute stores only pathname+search as `lastVisited`, so a
  // plan carried in router state is lost across a login bounce. Read ?plan=
  // first and fall back to the router state the dashboard entry still uses.
  const planFromQuery = new URLSearchParams(location.search || "").get("plan");
  const [billing, setBilling] = useState(
    (planFromQuery || location.state?.plan) === "monthly" ? "monthly" : "yearly"
  );
  const [stage, setStage] = useState("pick");
  const [clientSecret, setClientSecret] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const price = PLAN_PRICES[billing];
  const subtitle = billing === "yearly" ? "/ year" : "/ month";

  useEffect(() => {
    // Only bail if we have neither a company id nor a name to act on.
    // The public page is company-less by design — never bounce it home.
    if (!publicMode && !companyID && !companyName) {
      navigate("/");
    }
  }, [publicMode, companyID, companyName, navigate]);

  const handlePayClick = async () => {
    if (submitting || stage !== "pick") return;
    setSubmitting(true);
    setError(null);
    try {
      // TIP-647 — the public page decides what "proceed" means (login, claim,
      // or company picker) and runs checkout itself once it has a company.
      if (onProceed) {
        await onProceed({ plan: billing });
        return;
      }
      const data = await createVerifiedCheckoutSession({ companyID, token, plan: billing });
      if (!data?.client_secret) throw new Error("No client_secret returned");
      setClientSecret(data.client_secret);
      setStage("paying");
    } catch (e) {
      setError(
        e?.data?.detail ||
        `Couldn't start checkout (HTTP ${e?.status || "?"}). Please try again.`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePlan = () => {
    setStage("pick");
    setClientSecret(null);
    setError(null);
  };

  const handleSkipClick = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSkip?.();
    } catch (e) {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="verify-account-root"
      style={{ minHeight: publicMode ? undefined : "100vh", background: "var(--bg)" }}
    >
      {/* MiniHeader — mirrors VerifyScreen / ActivatedSuccess for stage
          continuity. The public page renders inside RootLayout, which already
          supplies the site header, so this would be a second one. */}
      {!publicMode && (
      <header style={{
        background: "#fff", borderBottom: "1px solid var(--line)",
        position: "sticky", top: 0, zIndex: 30,
      }}>
        <div className="container-wide" style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 32px", height: 68,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <TrevexLogo style={{ height: 32, width: "auto" }} />
            <div style={{ width: 1, height: 22, background: "var(--line)", margin: "0 4px" }} />
            <div className="small" style={{ color: "var(--ink-2)", fontWeight: 500 }}>
              Become a member
            </div>
          </div>
        </div>
      </header>
      )}

      <div className="container-wide" style={{ padding: "32px 32px 80px" }}>
        {/* Hero */}
        <div style={{
          position: "relative", borderRadius: 18, padding: "44px 40px",
          background: "linear-gradient(135deg, #0A2638 0%, #002940 60%, #0E3550 100%)",
          color: "#fff", overflow: "hidden", marginBottom: 28,
        }}>
          <div style={{
            position: "absolute", inset: 0, opacity: 0.35,
            background: "radial-gradient(60% 100% at 100% 0%, rgba(251,81,45,.6) 0%, transparent 60%)",
          }} />
          <div style={{
            position: "absolute", top: -120, right: -80, width: 380, height: 380, borderRadius: 999,
            background: "radial-gradient(circle, rgba(255,255,255,.08), transparent 60%)",
          }} />

          <div style={{
            position: "relative", display: "grid", gridTemplateColumns: "1fr auto",
            gap: 32, alignItems: "center",
          }}>
            <div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px",
                borderRadius: 99, background: "rgba(255,255,255,.1)",
                fontSize: 12, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase",
                marginBottom: 14, color: "#FFD4C5",
              }}>
                {/* "Final step" is true in the verify-account flow; a visitor
                    landing on the public page is at the FIRST step. */}
                <SparkIcon /> {publicMode || fromMembershipPage || !showCheckout ? "Membership" : "Final step"}
              </div>
              <h1 style={{
                color: "#fff", fontSize: 44, lineHeight: 1.05, letterSpacing: "-.03em",
                maxWidth: 620,
              }}>
                Become a <span style={{ color: "var(--orange)" }}>Member</span> of TREVEX
              </h1>
              <p style={{
                fontSize: 17, lineHeight: 1.5, marginTop: 14, color: "#C8D9E2", maxWidth: 540,
              }}>
                {publicMode ? (
                  <>
                    Stand out, win more business, and build trust with every party
                    you trade with. Membership attaches to a business profile
                    you've claimed — we'll help you pick or claim one at the next
                    step.
                  </>
                ) : access === "unclaimed" ? (
                  <>
                    <b style={{ color: "#fff" }}>{gateName}</b> hasn't been claimed
                    yet. Claiming is free, and it's the first step to becoming a
                    member.
                  </>
                ) : access === "otherOwner" ? (
                  <>
                    <b style={{ color: "#fff" }}>{gateName}</b> is managed by
                    another TREVEX account.
                  </>
                ) : access === "missing" ? (
                  <>This link doesn't point to a business on TREVEX.</>
                ) : access === "checking" ? (
                  <>Membership attaches to a business profile you own on TREVEX.</>
                ) : (
                  <>
                    You've claimed <b style={{ color: "#fff" }}>{companyName}</b>.
                    Become a member to stand out, win more business, and build trust
                    with every party you trade with.
                  </>
                )}
              </p>
              {/* A visitor arriving cold from an ad or a shared link has no idea
                  what TREVEX is — the page used to sit behind a claim, so it
                  could assume that context. It can't any more. */}
              {publicMode && (
                <p style={{
                  fontSize: 14, lineHeight: 1.5, marginTop: 12, color: "#9FB6C4", maxWidth: 540,
                }}>
                  TREVEX is the UAE business directory where buyers, suppliers and
                  insurers check who they're trading with. Every UAE business has a
                  profile — claiming yours is free.
                </p>
              )}
            </div>
            <BigVerifiedBadge />
          </div>
        </div>

        {/* Two-column: benefits + payment */}
        <div style={{
          display: "grid", gridTemplateColumns: "minmax(0,1fr) 420px", gap: 28,
          alignItems: "start",
        }}>
          {/* Benefits + plan */}
          <div>
            <div style={{ marginBottom: 16 }}>
              <span className="eyebrow">What Membership unlocks</span>
              <h3 style={{ fontSize: 22, marginTop: 6 }}>
                {publicMode
                  ? "Six reasons businesses become members"
                  : "Six reasons businesses upgrade"}
              </h3>
            </div>

            <div style={{
              display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12,
            }}>
              <BenefitCard
                icon={<BadgeIcon />}
                title="Free verification"
                body="Request the green ✓ verified badge at no extra cost — a member-only perk."
                highlighted
              />
              <BenefitCard
                icon={<RocketIcon />}
                title="Priority placement"
                body="Members show first when buyers search your industry or city."
              />
              <BenefitCard
                icon={<StarIcon />}
                title="Member ratings"
                body="Collect public ratings on your products and services from other TREVEX members."
              />
              <BenefitCard
                icon={<HandshakeIcon />}
                title="Payment endorsements"
                body={"Businesses you trade with can endorse you as a \"pays on time\" payer — visible on your profile."}
              />
              <BenefitCard
                icon={<NetworkIcon />}
                title="Partner program claim"
                body="Join the TREVEX referral network to earn commissions and unlock co-marketing."
              />
              {/* QA: on the PUBLIC page this slot used to describe what a claimed* profile already gives you — i.e. */}
              {publicMode ? (
                <BenefitCard
                  icon={<UnlockIcon />}
                  title="Member rate"
                  body="Ten percent off TREVEX products, including Web Profiles and reports."
                />
              ) : (
                <BenefitCard
                  icon={<UnlockIcon />}
                  title="All Claimed benefits"
                  body="Edit business details, respond to enquiries, manage signatories — everything in Claimed."
                />
              )}
            </div>

            {/* Trust strip */}
            <div style={{
              marginTop: 22, padding: "14px 18px", borderRadius: 10, background: "var(--slate-2)",
              display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-2)" }}>
                <SmallShield color="var(--success)" /> 30-day money back
              </div>
              <Sep />
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-2)" }}>
                <LockIcon /> Secure payment via Stripe
              </div>
              <Sep />
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-2)" }}>
                <BadgeIcon size={14} /> Cancel any time
              </div>
            </div>
          </div>

          {/* Payment card — stage='pick' shows the price + Pay/Skip; stage='paying'
              swaps in the Stripe Embedded Checkout iframe. Hero + benefits stay
              visible in either stage to keep context for the buyer. */}
          <div style={{ position: "sticky", top: 92 }}>
            <div className="card" style={{ padding: 24, borderColor: "var(--line-2)" }}>
              {!showCheckout ? (
                <AccessGateCard
                  access={access}
                  onClaim={() => navigate(`/claim/${companyID}`)}
                  onFindBusiness={() => navigate("/search")}
                  onBackToProfile={() => navigate(`/search/${companyID}`)}
                />
              ) : stage === "pick" ? (
                <>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginBottom: 14,
                  }}>
                    <div className="eyebrow">Membership plan</div>
                    <span className="chip chip-verified" style={{ height: 22 }}>
                      <CheckSm /> {publicMode ? "Most businesses choose this" : "Most businesses upgrade"}
                    </span>
                  </div>

                  {/* Billing toggle */}
                  <div style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr", padding: 4, borderRadius: 10,
                    background: "var(--slate-2)", marginBottom: 18,
                  }}>
                    {[
                      { id: "yearly", label: "Yearly", note: "Save ~16%" },
                      { id: "monthly", label: "Monthly", note: "" },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setBilling(opt.id)}
                        style={{
                          height: 38, borderRadius: 8, border: "none", fontWeight: 600, fontSize: 13,
                          background: billing === opt.id ? "#fff" : "transparent",
                          color: billing === opt.id ? "var(--ink)" : "var(--muted)",
                          boxShadow: billing === opt.id ? "var(--shadow-sm)" : "none",
                          cursor: "pointer", display: "flex", alignItems: "center",
                          justifyContent: "center", gap: 6,
                        }}
                      >
                        {opt.label}
                        {opt.note && (
                          <span className="chip" style={{
                            background: "var(--orange-soft)", color: "var(--orange)",
                            borderColor: "#FFD4C5", height: 18, fontSize: 10, padding: "0 6px",
                          }}>{opt.note}</span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Price */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 44, fontWeight: 800, letterSpacing: "-.03em", lineHeight: 1, display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Dirham /> {price.toLocaleString()}
                    </span>
                    <span className="small" style={{ marginLeft: 4 }}>{subtitle}</span>
                  </div>
                  <div className="small" style={{ marginBottom: 16 }}>
                    {billing === "yearly" ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                        Equivalent to <Dirham /> {Math.round(PLAN_PRICES.yearly / 12)} / month, billed annually
                      </span>
                    ) : (
                      "Billed monthly · upgrade or cancel any time"
                    )}
                  </div>

                  <div className="small" style={{ color: "var(--muted)", marginBottom: 18 }}>
                    Card details and any applicable VAT are collected on the
                    next step inside our secure checkout.
                  </div>

                  {error && (
                    <div className="small" style={{
                      padding: "10px 12px", borderRadius: 8, marginBottom: 12,
                      background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA",
                    }}>{error}</div>
                  )}

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePayClick}
                    disabled={submitting}
                    style={{
                      width: "100%", height: 52, fontSize: 15,
                      opacity: submitting ? 0.55 : 1,
                      cursor: submitting ? "not-allowed" : "pointer",
                    }}
                  >
                    {submitting ? (
                      <><span className="spinner-sm" /> Starting checkout…</>
                    ) : (
                      <>Pay & Become a Member <ArrowRight size={16} /></>
                    )}
                  </button>

                  {/* No claimed plan to stay on when nothing is claimed yet. */}
                  {!publicMode && (
                    <button
                      type="button"
                      onClick={handleSkipClick}
                      disabled={submitting}
                      style={{
                        width: "100%", marginTop: 10, padding: "12px", border: "none",
                        background: "transparent", color: "var(--muted)", fontSize: 13,
                        fontWeight: 500, cursor: submitting ? "not-allowed" : "pointer",
                        textDecoration: "underline", textUnderlineOffset: 3, fontFamily: "inherit",
                      }}
                    >
                      Skip for now — stay on Claimed plan
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginBottom: 14,
                  }}>
                    <div className="eyebrow">
                      Paying — {billing === "yearly" ? "Annual" : "Monthly"} plan
                    </div>
                    <button
                      type="button"
                      onClick={handleChangePlan}
                      disabled={submitting}
                      style={{
                        background: "transparent", border: "none", color: "var(--orange)",
                        fontSize: 12, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer",
                        padding: 0, fontFamily: "inherit", textDecoration: "underline",
                        textUnderlineOffset: 3,
                      }}
                    >
                      Change plan
                    </button>
                  </div>

                  {error && (
                    <div className="small" style={{
                      padding: "10px 12px", borderRadius: 8, marginBottom: 12,
                      background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA",
                    }}>{error}</div>
                  )}

                  {/* Stripe Embedded Checkout — Stripe owns this iframe, including
                      the Pay button, card validation, billing address capture,
                      and the post-payment redirect to return_url. */}
                  <div id="checkout" style={{ minHeight: 540 }}>
                    {clientSecret && (
                      <EmbeddedCheckoutProvider
                        stripe={stripePromise}
                        options={{ clientSecret }}
                      >
                        <EmbeddedCheckout />
                      </EmbeddedCheckoutProvider>
                    )}
                  </div>

                  {/* No claimed plan to stay on when nothing is claimed yet. */}
                  {!publicMode && (
                    <button
                      type="button"
                      onClick={handleSkipClick}
                      disabled={submitting}
                      style={{
                        width: "100%", marginTop: 10, padding: "12px", border: "none",
                        background: "transparent", color: "var(--muted)", fontSize: 13,
                        fontWeight: 500, cursor: submitting ? "not-allowed" : "pointer",
                        textDecoration: "underline", textUnderlineOffset: 3, fontFamily: "inherit",
                      }}
                    >
                      Skip for now — stay on Claimed plan
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentScreen;

// Helpers + atoms (ported from Verify Account.html so this file is
// self-contained; matches the AgentIntake / VerifyScreen idiom).

// TIP-650 — replaces the price card when the visitor can't buy membership for
// this company. Copy is keyed by the `access` state computed in PaymentScreen.
const GATE_COPY = {
  unclaimed: {
    eyebrow: "Claim first",
    title: "Claim this business to become a member",
    body: "Membership attaches to a business profile you own. Claiming is free — once it's done, come back here to become a member.",
  },
  otherOwner: {
    eyebrow: "Owner only",
    title: "This business is managed by another account",
    body: "Membership can only be bought by its owner. Find your own business to claim it or become a member.",
  },
  missing: {
    eyebrow: "Not found",
    title: "We couldn't find this business",
    body: "The link may be out of date. Search for your business to claim it or become a member.",
  },
  checking: {
    eyebrow: "Membership",
    title: "Checking your access…",
    body: "One moment while we confirm this business is yours.",
  },
};

const AccessGateCard = ({ access, onClaim, onFindBusiness, onBackToProfile }) => {
  const copy = GATE_COPY[access];
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 10 }}>{copy.eyebrow}</div>
      <h3 style={{ fontSize: 20, lineHeight: 1.25, marginBottom: 8 }}>{copy.title}</h3>
      <p className="small" style={{ color: "var(--ink-2)", marginBottom: 20 }}>{copy.body}</p>

      {access === "checking" && <span className="spinner-sm" aria-hidden="true" />}

      {access === "unclaimed" && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={onClaim}
          style={{ width: "100%", height: 52, fontSize: 15 }}
        >
          Claim this business — it's free <ArrowRight size={16} />
        </button>
      )}

      {(access === "otherOwner" || access === "missing") && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={onFindBusiness}
          style={{ width: "100%", height: 52, fontSize: 15 }}
        >
          Find your business <ArrowRight size={16} />
        </button>
      )}

      {access === "otherOwner" && (
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onBackToProfile}
          style={{ width: "100%", height: 44, marginTop: 10, fontSize: 14 }}
        >
          Back to profile
        </button>
      )}
    </div>
  );
};

const BenefitCard = ({ icon, title, body, highlighted }) => (
  <div className="card" style={{
    padding: 18,
    background: highlighted ? "linear-gradient(160deg, var(--orange-soft) 0%, #fff 70%)" : "#fff",
    borderColor: highlighted ? "#FFD4C5" : "var(--line)",
    display: "flex", gap: 12,
  }}>
    <div style={{
      flexShrink: 0, width: 36, height: 36, borderRadius: 8,
      background: highlighted ? "var(--orange)" : "var(--slate-2)",
      color: highlighted ? "#fff" : "var(--ink)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>{icon}</div>
    <div>
      <div style={{ fontWeight: 700, fontSize: 14.5, marginBottom: 4 }}>{title}</div>
      <div className="small" style={{ lineHeight: 1.5 }}>{body}</div>
    </div>
  </div>
);

const Sep = () => (
  <div style={{ width: 1, height: 14, background: "var(--line)" }} />
);

const BigVerifiedBadge = () => (
  <svg
    width="220" height="220" viewBox="0 0 220 220" fill="none"
    style={{ filter: "drop-shadow(0 16px 40px rgba(251,81,45,.4))" }}
  >
    <g>
      {Array.from({ length: 16 }).map((_, i) => {
        const angle = (i * 360) / 16;
        return (
          <rect
            key={i}
            x="106" y="14" width="8" height="22" rx="2"
            fill="#FB512D"
            transform={`rotate(${angle} 110 110)`}
          />
        );
      })}
    </g>
    <circle cx="110" cy="110" r="78" fill="#FFEEEA" />
    <circle cx="110" cy="110" r="78" stroke="#FB512D" strokeWidth="2" fill="none" opacity=".4" />
    <circle cx="110" cy="110" r="62" fill="#FB512D" />
    <path d="M82 112 L102 132 L142 88" stroke="#fff" strokeWidth="9"
      strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const ArrowRight = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M3 8h10m0 0L9 4m4 4l-4 4" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckSm = ({ size = 14, strokeColor = "currentColor", strokeW = 2.4 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M3 8.5L6.5 12L13 4.5" stroke={strokeColor} strokeWidth={strokeW}
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SparkIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z" fill="currentColor" />
  </svg>
);

const SmallShield = ({ color = "var(--muted)" }) => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5l5 1.8v4.4c0 3.2-2.2 5.6-5 6.8-2.8-1.2-5-3.6-5-6.8V3.3l5-1.8z"
      stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M5.5 8L7.2 9.7L10.5 6.4" stroke={color} strokeWidth="1.4"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="3" y="7" width="10" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const BadgeIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 2l2.5 1.5h3l1 3 2.5 2.5-1.5 2.5L21 14l-1 3h-3L14.5 19 12 22l-2.5-3L7 17H4l-1-3 1-2.5L2 9l2.5-2.5 1-3h3L12 2z"
      stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M8 12l3 3 5-5" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RocketIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M14 6l4 4-9 9-3-1-1-3 9-9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M14 6c0-2 2-4 5-4-0 3-2 5-4 5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M5 19l-2 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M12 3l2.7 5.5 6.3.9-4.5 4.4 1 6.2L12 17l-5.5 3 1-6.2L3 9.4l6.3-.9L12 3z"
      stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const HandshakeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M3 12l4-4 4 2 4-2 6 4-3 5-3-2-3 3-3-2-3 2-3-6z"
      stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M11 14l2-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const NetworkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="5" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="19" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 7.5v6m-5 2L11 13m6 2.5L13 13"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const UnlockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="4" y="11" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 11V7a4 4 0 018 0" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="16.5" r="1.5" fill="currentColor" />
  </svg>
);
